export type TelegramWriteAccessChoice = "allowed" | "cancelled";
export type TelegramWriteAccessRequestResult = TelegramWriteAccessChoice | "unsupported" | "unavailable";

export type TelegramWriteAccessBridge = {
  requestWriteAccess?(callback?: (allowed: boolean) => void): void;
  onEvent?(event: "writeAccessRequested", callback: (payload: unknown) => void): void;
  offEvent?(event: "writeAccessRequested", callback: (payload: unknown) => void): void;
};

const REQUEST_TIMEOUT_MS = 15_000;

function eventChoice(payload: unknown): TelegramWriteAccessChoice | null {
  if (typeof payload !== "object" || payload === null) return null;
  const status = (payload as { status?: unknown }).status;
  if (status === "allowed") return "allowed";
  if (status === "cancelled") return "cancelled";
  return null;
}

export function requestTelegramWriteAccess(
  bridge: TelegramWriteAccessBridge | null,
  timeoutMs = REQUEST_TIMEOUT_MS,
): Promise<TelegramWriteAccessRequestResult> {
  if (typeof bridge?.requestWriteAccess !== "function") return Promise.resolve("unsupported");
  const request = bridge.requestWriteAccess.bind(bridge);
  return new Promise((resolve) => {
    let settled = false;
    let timeout: ReturnType<typeof setTimeout> | null = null;
    const cleanup = () => {
      if (timeout !== null) clearTimeout(timeout);
      bridge.offEvent?.("writeAccessRequested", onEvent);
    };
    const finish = (result: TelegramWriteAccessRequestResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };
    const onEvent = (payload: unknown) => {
      const choice = eventChoice(payload);
      if (choice !== null) finish(choice);
    };

    bridge.onEvent?.("writeAccessRequested", onEvent);
    timeout = setTimeout(() => finish("unavailable"), timeoutMs);
    try {
      request((allowed) => finish(allowed ? "allowed" : "cancelled"));
    } catch {
      finish("unavailable");
    }
  });
}
